import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getFreeStartTimes } from "../APIs/availability";

// Guess user's timezone; we pass it to the backend but keep the UI simple (no zone selector for now)
const defaultZone = (() => {
  try {
    return (
      Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York"
    );
  } catch {
    return "America/New_York";
  }
})();

// Format today's date (YYYY-MM-DD) in the user's zone
function todayInZone(zone) {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: zone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return fmt.format(new Date()); // e.g. "2025-09-06"
}

// Turn "2025-09-06T09:00:00-04:00[America/New_York]" into a friendly "09:00 AM"
function labelFromZdtString(zdtString) {
  const isoWithOffset = zdtString.split("[")[0]; // keep offset, drop region
  const d = new Date(isoWithOffset);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function ViewAvailabilities({ providerId: providerIdProp }) {
  const { providerId: providerIdParam } = useParams();
  const providerId = providerIdProp ?? providerIdParam;

  const [date, setDate] = useState(() => todayInZone(defaultZone));
  const [loading, setLoading] = useState(false);
  const [startTimes, setStartTimes] = useState([]); // string[] of ZonedDateTime
  const [error, setError] = useState("");

  const headerDate = useMemo(() => {
    // Pretty date label in user’s zone
    const [y, m, d] = date.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0)); // noon UTC → stable conversion
    return dt.toLocaleDateString([], {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }, [date]);

  async function fetchTimes() {
    if (!providerId) {
      setError("Missing providerId");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await getFreeStartTimes(Number(providerId), {
        date,
        zone: defaultZone,
        // telehealth: undefined // add if you later want a filter
      });
      setStartTimes(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.message || "Failed to load start times");
      setStartTimes([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchTimes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId, date]);

  return (
    <div className="max-w-3xl p-4 space-y-4">
      {/* Back button at top left */}
      <button
        onClick={() => window.history.back()}
        className="mb-4 text-sm px-3 py-1.5 rounded bg-gray-200 hover:bg-gray-300 flex items-center gap-1"
      >
        ← Back
      </button>
      <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
        <div className="grow">
          <label className="block mb-1 font-medium">Select a date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full border rounded px-3 py-2"
          />
        </div>
        <button
          onClick={fetchTimes}
          className="px-4 py-2 rounded bg-gray-800 text-white hover:bg-black"
        >
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      <div className="flex items-baseline justify-between">
        <h4 className="text-lg font-semibold">Start times for {headerDate}</h4>
        <span className="text-xs text-gray-500">{defaultZone}</span>
      </div>

      {error && (
        <div className="p-3 rounded bg-red-50 text-red-700 border border-red-200">
          {error}
        </div>
      )}

      {!loading && startTimes.length === 0 && !error && (
        <p className="text-gray-600">No FREE start times found for this day.</p>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {startTimes.map((zdt, idx) => (
          <Link
            key={`${zdt}-${idx}`}
            //to={`/book/${encodeURIComponent(providerId)}/${encodeURIComponent(zdt)}`}
            to={`/book/availability/${encodeURIComponent(providerId)}/${encodeURIComponent(zdt)}`}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 text-center"
            title={zdt}
          >
            {labelFromZdtString(zdt)}
          </Link>
        ))}
      </div>
    </div>
  );
}
